import { ChangeDetectionStrategy, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { ModuleType } from '@fi-shared/types/module-type';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { AppTextService } from '@fi-shared/app-text';
import { ILightWeightObject } from '@fi-shared/models';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { ExtendedRoleRightService } from '../services/extended-role-right.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiSnackBarService } from '@fi-shared/fi-shared-services';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { IRoleRelations } from '@fi-app/role/role';
import { IExtendSharingObjectItem } from '@fi-app/components/extend-sharing/extend-sharing';
import { fiTextToHtml } from '@fi-shared/utils';

@Component({
    selector: 'fi-role-relations',
    templateUrl: './role-relations.component.html',
    styleUrls: ['./role-relations.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-role-relations' },
    standalone: false,
})
export class RoleRelationsComponent implements OnInit {
    @Input() roleRelations: IRoleRelations;
    @Input() allRoles: ILightWeightObject[];
    @Input() shareExistingContent: boolean;

    shareRoles: IExtendSharingObjectItem[] = [];
    manageRoles: IExtendSharingObjectItem[] = [];

    constructor(
        private statisticService: StatisticService,
        private appTextService: AppTextService,
        private saveChangesWarningService: SaveChangesWarningService,
        private extendedRoleRightService: ExtendedRoleRightService,
        private loadingService: LoadingService,
        private snackBar: FiSnackBarService,
        private stateService: FiStateService,
    ) {}

    ngOnInit() {
        this.setExtendedSharingData();

        this.saveChangesWarningService.start(() => ({
            shareRoles: this.shareRoles,
            manageRoles: this.manageRoles,
        }));

        this.statisticService.Visit(ModuleType.Core, ObjectTypes.Role, this.roleRelations.RoleId);
    }

    private setExtendedSharingData() {
        this.shareRoles = this.getExtendedSharingItemsFromRoles(this.roleRelations.RoleIdsCanShareWithThisRole);
        this.manageRoles = this.getExtendedSharingItemsFromRoles(this.roleRelations.RoleIdsCanManageThisRole, true);
    }

    private getExtendedSharingItemsFromRoles(
        checkedRoleIds: number[],
        preventDisablingCurrentRole: boolean = false,
    ): IExtendSharingObjectItem[] {
        return this.allRoles.map((role: ILightWeightObject): IExtendSharingObjectItem => {
            return {
                id: role.Id,
                objectType: ObjectTypes.Role,
                label: role.Name,
                isChecked: checkedRoleIds.includes(role.Id),
                isDisabled: !preventDisablingCurrentRole && role.Id === this.roleRelations.RoleId,
            };
        });
    }

    saveRoleRelationsClick() {
        const roleRelations: IRoleRelations = Object.assign(this.roleRelations);
        roleRelations.RoleIdsCanShareWithThisRole = this.getCheckedRoleIds(this.shareRoles, roleRelations.RoleId);
        roleRelations.RoleIdsCanManageThisRole = this.getCheckedRoleIds(this.manageRoles, roleRelations.RoleId);

        this.loadingService
            .trackPromise(this.extendedRoleRightService.saveRoleRelations(this.roleRelations))
            .then(() => {
                this.saveChangesWarningService.stop();

                this.snackBar.showSnackbar({
                    textMessage: this.appTextService.get('role.roles_updated'),
                    buttonText: this.appTextService.get('common.dismiss'),
                });

                if (this.shareExistingContent) {
                    this.stateService.go(
                        FiStateNames.ExtendSharing.Edit,
                        {
                            objectId: this.roleRelations.RoleId,
                            objectType: ObjectTypes.Role,
                        },
                        { location: 'replace' },
                    );
                } else {
                    this.stateService.go(FiStateNames.Role.List);
                }
            });
    }

    private getCheckedRoleIds(sharingRoles: IExtendSharingObjectItem[], roleId: number): number[] {
        return sharingRoles
            .filter((item: IExtendSharingObjectItem) => item.isChecked)
            .map((item: IExtendSharingObjectItem) => item.id);
    }

    protected getShareDescriptionText(): string {
        return this.appTextService.get('extended_rights.role_share_content_description', {
            '{0}': fiTextToHtml(this.roleRelations.RoleName),
        });
    }

    protected getManageDescriptionText(): string {
        return this.appTextService.get('extended_rights.role_manage_description', {
            '{0}': fiTextToHtml(this.roleRelations.RoleName),
        });
    }
}
